import { hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { addYears } from 'date-fns';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import DatePickerDropdown from 'garaje/components/date-picker-dropdown';
import DatePickerOptions from 'garaje/components/date-picker-options';
import type DateLimits from 'garaje/types/date-time/date-limits';

interface RecurringInvitesEndSignature {
  Args: {
    error?: string;
    isEditInvite?: boolean;
    isReadonly?: boolean;
    onChange?: (value: Date) => void;
    timezone?: string;
    value?: Date | null;
    dateLimits?: DateLimits;
    shortcuts?: string[];
    isRecurringGroupInvite?: boolean;
  };
  Element: HTMLDivElement;
}

export default class RecurringInvitesEnd extends Component<RecurringInvitesEndSignature> {
  get shortcuts(): string[] {
    if (this.args.shortcuts) return this.args.shortcuts;
    return ['today', 'oneYearFromNow'];
  }

  get value(): Date | undefined {
    return this.args.value ?? undefined;
  }

  get dateLimits(): DateLimits {
    if (this.args.dateLimits) return this.args.dateLimits;

    const today = new Date();
    const oneYearFromNow = addYears(today, 1);
    return {
      startDate: 0,
      endDate: Math.floor(oneYearFromNow.getTime() / 1000),
    };
  }

  @action
  handleDateRangeSelected(_date1: Date, date2: Date): void {
    this.args.onChange?.(date2);
  }

  @action
  handleDateClicked(date: Date): void {
    this.args.onChange?.(date);
  }

  get initiallyOpened(): boolean {
    if (this.args.isReadonly || this.args.isEditInvite) {
      return false;
    }
    return true;
  }

  <template>
    <div class="entry-time-input" ...attributes>
      <label for="rruleUntil">
        Ends
        <span class="requiredField" />

        {{#if @isRecurringGroupInvite}}
          <span>
            <img
              width="16"
              height="16"
              src="/assets/images/gray-info.svg"
              class="ml-2"
              id="multiple-hosts-invite-info"
              alt="info"
            />
            <EmberTooltip @side="top">
              Recurring group invites can go up to three months.
            </EmberTooltip>
          </span>
        {{/if}}
      </label>
      <DatePickerDropdown
        data-test-invite-rrule-until
        @dateFormat="EEEE, MMM d, yyyy"
        @dateLimits={{this.dateLimits}}
        @dropdownOptions={{hash
          id="rruleUntil"
          triggerClass="!block !px-2"
          initiallyOpened=this.initiallyOpened
          disabled=@isReadonly
        }}
        @emptyDatePlaceholder="Never"
        @shortcuts={{this.shortcuts}}
        @timezone={{@timezone}}
        @endDate={{this.value}}
        @setEndDate={{this.handleDateClicked}}
        @setStartDate={{this.handleDateClicked}}
        @onDateRangeSelected={{this.handleDateRangeSelected}}
        @onDateSelected={{this.handleDateRangeSelected}}
        @startDate={{this.value}}
      >
        <:date-picker-dropdown-left-sidebar as |sidebar|>
          <DatePickerOptions
            @options={{sidebar.options}}
            @selectedOption={{sidebar.selectedOption}}
            @highlightedOption={{sidebar.highlightedOption}}
            @dropdownId={{sidebar.dropdownId}}
            @highlightedIndex={{sidebar.highlightedIndex}}
            @onOptionSelect={{sidebar.onOptionSelect}}
            @onListboxFocus={{sidebar.onListboxFocus}}
            @close={{sidebar.close}}
          />
        </:date-picker-dropdown-left-sidebar>
      </DatePickerDropdown>
      {{#if @error}}
        <div data-test-input-error class="bottom-error !mt-1">
          {{@error}}
        </div>
      {{/if}}
    </div>
  </template>
}
